import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import MobileShell from '../../components/MobileShell';
import { CheckCircle, Circle, Clock, AlertCircle, User as UserIcon, MessageSquare, ChevronRight } from 'lucide-react';

const FILTERS = [
  { key: 'mine', label: 'My tasks' },
  { key: 'open', label: 'Open' },
  { key: 'done', label: 'Done' },
  { key: 'all',  label: 'All' },
];

const PRIORITIES = [
  { key: 'high',   label: 'High',   dot: 'bg-red-500',   chip: 'text-red-700' },
  { key: 'normal', label: 'Medium', dot: 'bg-amber-500', chip: 'text-amber-700' },
  { key: 'low',    label: 'Low',    dot: 'bg-slate-400', chip: 'text-slate-500' },
];
const prio = (k) => PRIORITIES.find(p => p.key === k) || PRIORITIES[1];

/**
 * MobileTasks — list only. Detail on dedicated page (/m/tasks/:id).
 * No more bottom-sheet modal (per user's "no modals on mobile" rule).
 */
export default function MobileTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('mine');

  const load = () => api.get('/tasks').then(r => setTasks(r.data));
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    return tasks.filter(t => {
      if (filter === 'mine') return t.assignee_user_id === user?.user_id && t.status !== 'done';
      if (filter === 'open') return t.status !== 'done';
      if (filter === 'done') return t.status === 'done';
      return true;
    }).sort((a, b) => {
      const pa = PRIORITIES.findIndex(p => p.key === a.priority);
      const pb = PRIORITIES.findIndex(p => p.key === b.priority);
      return pa - pb || ((a.due_date || '') > (b.due_date || '') ? 1 : -1);
    });
  }, [tasks, filter, user]);

  const complete = async (id, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    await api.post(`/tasks/${id}/complete`);
    load();
  };

  const groups = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7);
    const g = { overdue: [], today: [], week: [], later: [], done: [] };
    for (const t of visible) {
      if (t.status === 'done') { g.done.push(t); continue; }
      if (!t.due_date) { g.later.push(t); continue; }
      const d = new Date(t.due_date);
      if (d < today) g.overdue.push(t);
      else if (d.toDateString() === today.toDateString()) g.today.push(t);
      else if (d < weekEnd) g.week.push(t);
      else g.later.push(t);
    }
    return g;
  }, [visible]);

  const groupOrder = [
    { key: 'overdue', label: 'Overdue', accent: 'text-red-700' },
    { key: 'today',   label: 'Today',   accent: 'text-amber-700' },
    { key: 'week',    label: 'This week', accent: 'text-slate-800' },
    { key: 'later',   label: 'Later',   accent: 'text-slate-500' },
    { key: 'done',    label: 'Done',    accent: 'text-emerald-700' },
  ];

  return (
    <MobileShell noHeader>
      <div className="pt-3 pb-4">
        <h1 className="font-serif text-3xl mb-1">Tasks</h1>
        <p className="text-xs text-slate-500">{visible.length} shown</p>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-4 no-scrollbar" data-testid="m-tasks-filters">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`m-tasks-filter-${f.key}`}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              filter === f.key ? 'bg-[#1A2530] text-white shadow-md' : 'bg-white/70 text-slate-700 border border-white/60'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-4 pb-8" data-testid="m-tasks-list">
        {visible.length === 0 && (
          <div className="text-center py-16 text-sm text-slate-400">
            {filter === 'mine' ? 'No tasks assigned to you. Nice.' : 'No tasks match this filter.'}
          </div>
        )}
        {groupOrder.map(({ key, label, accent }) => {
          const rows = groups[key];
          if (!rows?.length) return null;
          return (
            <section key={key} data-testid={`m-tasks-group-${key}`}>
              <div className={`text-[10px] uppercase tracking-widest ${accent} font-bold mb-2 flex items-center gap-2`}>
                {label} <span className="text-slate-400 font-medium">· {rows.length}</span>
              </div>
              <div className="space-y-2">
                {rows.map(t => <TaskRow key={t.id} t={t} onComplete={complete}/>)}
              </div>
            </section>
          );
        })}
      </div>
    </MobileShell>
  );
}

function TaskRow({ t, onComplete }) {
  const p = prio(t.priority);
  const overdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== 'done';
  return (
    <Link
      to={`/m/tasks/${t.id}`}
      data-testid={`m-task-${t.id}`}
      className="w-full text-left p-4 rounded-2xl bg-white/85 border border-white/70 shadow-sm flex items-start gap-3 active:scale-[0.98] transition-transform"
    >
      <button
        onClick={(e) => onComplete(t.id, e)}
        className="text-slate-400 hover:text-emerald-600 mt-0.5 flex-shrink-0"
        data-testid={`m-task-check-${t.id}`}
      >
        {t.status === 'done' ? <CheckCircle size={22} className="text-emerald-600"/> : <Circle size={22}/>}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${t.status === 'done' ? 'line-through text-slate-400' : 'text-slate-800'}`}>{t.title}</div>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
          <span className={`inline-flex items-center gap-1 font-medium ${p.chip}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`}/> {p.label}
          </span>
          {t.due_date && (
            <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}>
              {overdue ? <AlertCircle size={11}/> : <Clock size={11}/>}
              {new Date(t.due_date).toLocaleDateString()}
            </span>
          )}
          {t.assignee_name && <span className="flex items-center gap-1"><UserIcon size={11}/> {t.assignee_name}</span>}
          {(t.notes?.length || 0) > 0 && <span className="flex items-center gap-1"><MessageSquare size={11}/> {t.notes.length}</span>}
        </div>
      </div>
      <ChevronRight size={14} className="text-slate-400 mt-1 flex-shrink-0"/>
    </Link>
  );
}
